import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { AppBar, Avatar, Box, Drawer, IconButton, List, ListItem, ListItemButton, ListItemText, Divider, Menu, MenuItem, ListItemIcon, Toolbar, Tooltip, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, LogOut as LogOutIcon, PanelLeftOpen, PanelLeftClose, Search as SearchIcon } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import ModuleNavBar from './ModuleNavBar';
import ModuleSidebar from './ModuleSidebar';
import type { ModuleId } from '../theme/moduleColors';
import { BRAND } from '../theme';
import type { ModuleNavigation } from './ModuleSidebar';
import { jylawNavigation } from '../modules/jylaw/navigation';
import NavCutoverSnackbar from '../components/nav/NavCutoverSnackbar';
// NOTE: C3RetirementSnackbar import deferred to spec-49 W5 — component file not yet shipped.
// Was prematurely added in commit 870bcf5a9 (spec-48 SEC fix); broke clean-HEAD build.
import NotificationBell from '../components/NotificationBell';
import { useGlobalSearchContext } from '../contexts/GlobalSearchContext';
import { LanguageToggle } from '../shared/components/LanguageToggle';
import { wikiNavigation } from '../modules/wiki/navigation';
import { setupRegistrationNavigation } from '../modules/setup-registration/navigation';
import { transactionsNavigation } from '../modules/transactions/navigation';
import { postIncorporationNavigation } from '../modules/post-incorporation/navigation';
import { retainerNavigation } from '../modules/retainer/navigation';
import { courtNavigation } from '../modules/court/navigation';
import { delegationNavigation } from '../modules/delegation/navigation';
import { crmNavigation } from '../modules/crm/navigation';
import { documentsNavigation } from '../modules/documents/navigation';
import { legalServicesNavigation } from '../modules/legal-services/navigation';

const DRAWER_WIDTH = 260;
const MODULE_BAR_HEIGHT = 48;

const MODULE_NAV_MAP: Record<ModuleId, ModuleNavigation> = {
  'jylaw': jylawNavigation,
  'setup-registration': setupRegistrationNavigation,
  'transactions': transactionsNavigation,
  'post-incorporation': postIncorporationNavigation,
  'retainer': retainerNavigation,
  'court': courtNavigation,
  'delegation': delegationNavigation,
  wiki: wikiNavigation,
  crm: crmNavigation,
  documents: documentsNavigation,
  'legal-services': legalServicesNavigation,
};

// Routes that belong to a module but don't share its URL prefix.
// Keys may be a single segment ("dashboard") or a two-segment path
// ("admin/document-templates") — two-segment keys are checked first so
// they can override a more general single-segment rule.
const ROUTE_MODULE_MAP: Record<string, ModuleId> = {
  'email-inbox': 'jylaw',
  'ocr-review': 'jylaw',
  'admin': 'jylaw',
  'admin/document-templates': 'documents',
  'dashboard': 'jylaw',
  'dashboard/expiries': 'jylaw',  // spec-45 W2
  'dashboard/ops': 'jylaw',       // spec-47 W3
  'me': 'jylaw',
  'tasks': 'jylaw',  // spec-39 W4.6 — cross-module My Tasks page
  'suggestions': 'jylaw',  // spec-42 W6 — AI suggestions feed
  'compliance': 'jylaw',
  'conflict': 'jylaw',
};

function detectModule(pathname: string): ModuleId | null {
  const segments = pathname.split('/').filter(Boolean);
  // Two-segment lookups first: lets an explicit
  // "admin/document-templates" override the single-segment "admin" rule.
  if (segments.length >= 2) {
    const twoSegSlash = `${segments[0]}/${segments[1]}`;
    if (twoSegSlash in ROUTE_MODULE_MAP) return ROUTE_MODULE_MAP[twoSegSlash];
    const twoSegDash = `${segments[0]}-${segments[1]}`;
    if (twoSegDash in MODULE_NAV_MAP) return twoSegDash as ModuleId;
  }
  if (segments.length >= 1) {
    const seg = segments[0];
    if (seg in MODULE_NAV_MAP) return seg as ModuleId;
    if (seg in ROUTE_MODULE_MAP) return ROUTE_MODULE_MAP[seg];
  }
  return null;
}

const PortalShell: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation('common');
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const { open: openSearch } = useGlobalSearchContext();
  const isMacPlatform = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('jylaw-sidebar-collapsed') === 'true'; } catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('jylaw-sidebar-collapsed', String(next)); } catch {}
      return next;
    });
  };
  const rtl = i18n.language === 'ar';

  const activeModule = useMemo(() => detectModule(location.pathname), [location.pathname]);

  // FIX #8 — inject role-gated /conflict nav item for managing_partner
  const moduleNav = useMemo((): ModuleNavigation | null => {
    if (!activeModule) return null;
    const base = MODULE_NAV_MAP[activeModule];
    if (activeModule === 'jylaw' && user?.role === 'managing_partner') {
      // Append 'Compliance' section with conflict checks if not already present
      const hasConflict = base.some((s) => s.items.some((i) => i.path === '/conflict'));
      if (!hasConflict) {
        return [
          ...base,
          {
            title: t('nav.compliance') || 'Compliance',
            items: [{ path: '/conflict', label: t('nav.conflict_checks') || 'Conflict checks' }],
          },
        ];
      }
    }
    return base;
  }, [activeModule, user?.role]);

  const handleLogout = async () => {
    setUserMenuAnchor(null);
    await logout();
    navigate('/login');
  };

  const showSidebar = !!moduleNav;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: 'background.paper', color: 'text.primary', borderBottom: '2px solid', borderColor: 'secondary.main', zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ minHeight: '56px !important', px: 2 }}>
          {isMobile && (
            <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 1, color: 'text.primary' }} aria-label="Open navigation menu">
              <MenuIcon size={18} />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: 'transparent', border: '1px solid', borderColor: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: 'secondary.main', fontWeight: 700, fontSize: '0.85rem', fontFamily: '"Source Serif 4", "Source Serif Pro", Georgia, serif', letterSpacing: '0.5px' }}>JY</Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'primary.main', fontFamily: '"Source Serif 4", "Source Serif Pro", Georgia, serif', letterSpacing: '0.3px', fontSize: '1.05rem' }}>{BRAND.displayName}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {/* spec-46 W3 — global search affordance */}
          <Box
            onClick={openSearch}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              mr: 0.5,
              border: '1px solid',
              borderColor: '#E3E7EC',
              borderRadius: 1.5,
              cursor: 'pointer',
              bgcolor: '#FAFAF7',
              color: '#4A5568',
              fontSize: '0.8rem',
              userSelect: 'none',
              '&:hover': { borderColor: '#C5CDD7', bgcolor: '#F0F2F5' },
              transition: 'all 0.15s',
              minWidth: 120,
            }}
          >
            <SearchIcon size={13} />
            <Typography sx={{ fontSize: '0.78rem', color: '#9CA3AF', flexGrow: 1 }}>
              {t('search.header_button', 'Search')}
            </Typography>
            <Typography component="kbd" sx={{ fontSize: '0.65rem', color: '#9CA3AF', fontFamily: 'monospace', bgcolor: '#E3E7EC', px: 0.5, py: 0.1, borderRadius: 0.5, border: '1px solid #C5CDD7', lineHeight: 1.4 }}>
              {isMacPlatform ? '⌘K' : 'Ctrl+K'}
            </Typography>
          </Box>
          <NotificationBell />
          <LanguageToggle size="small" />
          <Tooltip title={user?.username ?? 'Account'}>
            <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} size="small">
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(11, 37, 69, 0.04)', color: 'primary.main', fontSize: '0.85rem', fontWeight: 600, border: '1px solid', borderColor: 'secondary.main' }}>
                {user?.username?.charAt(0).toUpperCase() ?? 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={userMenuAnchor} open={!!userMenuAnchor} onClose={() => setUserMenuAnchor(null)}>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogOutIcon size={16} /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ mt: '58px', position: 'fixed', top: 0, left: 0, right: 0, zIndex: theme.zIndex.drawer, display: { xs: 'none', md: 'block' } }}>
        <ModuleNavBar activeModule={activeModule} />
      </Box>

      <Box sx={{ display: 'flex', mt: { xs: '58px', md: `${58 + MODULE_BAR_HEIGHT}px` }, minHeight: { xs: 'calc(100vh - 58px)', md: `calc(100vh - ${58 + MODULE_BAR_HEIGHT}px)` } }}>
        {/* Desktop sidebar — only when module has nav AND not collapsed */}
        {showSidebar && !isMobile && !sidebarCollapsed && (
          <Box sx={{ width: DRAWER_WIDTH, flexShrink: 0, borderRight: '1px solid', borderColor: '#E3E7EC', bgcolor: '#FFFFFF', overflowY: 'auto', height: `calc(100vh - ${58 + MODULE_BAR_HEIGHT}px)`, position: 'sticky', top: `${58 + MODULE_BAR_HEIGHT}px` }}>
            <ModuleSidebar moduleId={activeModule!} navigation={moduleNav!} onCollapse={toggleSidebar} isRtl={rtl} />
          </Box>
        )}
        {/* Floating expand handle when sidebar is collapsed */}
        {showSidebar && !isMobile && sidebarCollapsed && (
          <Tooltip title={rtl ? 'إظهار القائمة' : 'Show sidebar'} placement={rtl ? 'left' : 'right'}>
            <IconButton
              onClick={toggleSidebar}
              aria-label={rtl ? 'إظهار القائمة الجانبية' : 'Show sidebar'}
              sx={{
                position: 'fixed',
                top: `${58 + MODULE_BAR_HEIGHT + 12}px`,
                insetInlineStart: 0,
                bgcolor: 'background.paper',
                color: 'primary.main',
                border: '1px solid',
                borderColor: 'divider',
                borderInlineStartWidth: 0,
                borderStartStartRadius: 0,
                borderEndStartRadius: 0,
                borderStartEndRadius: 6,
                borderEndEndRadius: 6,
                boxShadow: '0 2px 6px rgba(11, 37, 69, 0.08)',
                zIndex: theme.zIndex.drawer - 1,
                width: 28, height: 36, p: 0.5,
                '&:hover': { bgcolor: 'rgba(11, 37, 69, 0.04)', color: 'primary.dark' },
              }}
            >
              {rtl ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
            </IconButton>
          </Tooltip>
        )}
        {/* Mobile drawer — always available; uses module nav when present, else default app nav */}
        {isMobile && (
          <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, mt: '58px', bgcolor: '#FFFFFF', borderRight: '1px solid #E3E7EC' } }}>
            {moduleNav && activeModule ? (
              <ModuleSidebar moduleId={activeModule} navigation={moduleNav} />
            ) : (
              <Box sx={{ width: '100%', py: 1 }}>
                <Box sx={{ px: 2, py: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#0B2545', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Navigation</Typography>
                </Box>
                <Divider sx={{ borderColor: '#E3E7EC', mb: 1 }} />
                <List dense>
                  {[
                    { path: '/', label: t('nav.dashboard') },
                    { path: '/me', label: t('nav.myWorkspace') },
                    // — Service-line tabs (DEC-48-2 order) spec-48 W3
                    { path: '/retainer/list', label: t('modules.retainer.label', 'Retainer') },
                    { path: '/transactions/matters', label: t('nav.transactions', 'Transactions') },
                    { path: '/setup-registration/matters', label: t('nav.setup_registration', 'Setup — Registration') },
                    { path: '/post-incorporation/matters', label: t('nav.post_incorporation', 'Post-Incorporation') },
                    { path: '/legal-services/matters', label: t('nav.legal_services', 'Legal Services') },
                    { path: '/court/litigation', label: t('nav.court') },
                    // — Cross-cutting tabs
                    { path: '/crm/pipeline', label: t('nav.crm') },
                    { path: '/documents/browse', label: t('nav.documents') },
                    { path: '/wiki', label: t('nav.wiki') },
                    { path: '/delegation/settings', label: t('nav.delegation') },
                    // — Utility
                    { path: '/billing/invoices', label: t('nav.billing') },
                  ].map((item) => (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        component={RouterLink}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        sx={{ px: 2, py: 0.75, '&:hover': { bgcolor: 'rgba(11, 37, 69, 0.08)' } }}
                      >
                        <ListItemText primary={item.label} primaryTypographyProps={{ sx: { color: '#0E1116', fontSize: '0.88rem' } }} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Drawer>
        )}
        <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: '#FAFAF7', minHeight: '100%', overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
      {/* spec-48 W5 — one-time nav cutover onboarding message */}
      <NavCutoverSnackbar />
      {/* spec-49 W5 C3RetirementSnackbar will mount here once spec-49 W5 ships the component. */}
    </Box>
  );
};

export default PortalShell;
